import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ukDayYear } from "@/lib/time";
import { RemoveDialog } from "@/components/hub/RemoveDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Loader2, Pencil, Trash2, Link2, Copy, Check, RefreshCw, FileSignature, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { COMPANY_TYPES, FINANCE_TYPES, COUNTRIES, DELIVERY_METHODS } from "./constants";
import { MoneyField, PercentField, CountField } from "@/components/ui/number-fields";
import { AgreementAdminDialog } from "./AgreementAdminDialog";
import { CapacityCard } from "@/components/hub/CapacityCard";
import { DeclineInsights } from "@/components/hub/DeclineInsights";
import { MutedPill, MutedNotice } from "@/components/hub/MutedPill";
import { TermsChangePrompt } from "@/components/hub/TermsChangePrompt";
import { AssignManagerDialog } from "@/components/hub/AssignManagerDialog";
import {
  Btn, CellMain, CellSub, Empty, Lname, Money, PageHead, Panel, Sec, St, StatStrip, Tag, Tb, Td, Th,
} from "@/components/portal/crm/kit";

/** A square icon control that belongs to the v3 system rather than Tailwind. */
const IconBtn = ({ children, onClick, title, testId, danger }) => (
  <button onClick={onClick} title={title} data-testid={testId}
          style={{ display: "grid", placeItems: "center", width: 32, height: 32, border: 0, borderRadius: 9,
            background: "transparent", cursor: "pointer", color: danger ? "var(--red)" : "var(--mut)" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "var(--hover)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
    {children}
  </button>
);

export default function HubLenders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | { ... } | "new"
  const [toDelete, setToDelete] = useState(null);
  const [termsMoved, setTermsMoved] = useState(null); // { party, change }
  const [managers, setManagers] = useState([]);
  const [assignFor, setAssignFor] = useState(null);

  const loadManagers = () => api.get("/hub/managers")
    .then((r) => setManagers(r.data.items || [])).catch(() => setManagers([]));
  useEffect(() => { loadManagers(); }, []);
  const managerName = (id) => managers.find((m) => m.id === id)?.name;

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/hub/lenders"); setRows(r.data.items || []); }
    catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [agreementFor, setAgreementFor] = useState(null);

  const regenerate = async (id) => {
    try {
      const r = await api.post(`/hub/lenders/${id}/invite`);
      setRows((rs) => rs.map((x) => x.id === id ? { ...x, invite: r.data.invite, activated: false } : x));
      toast.success("Invite regenerated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendAccessLink = async (id) => {
    try {
      const r = await api.post(`/hub/partners/lender/${id}/access-link`);
      toast.success(r.data.action === "reset" ? "Password reset link emailed + texted" : "Set-up link emailed + texted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the link"); }
  };

  const active = rows.filter((r) => r.status === "active").length;
  const signed = rows.filter((r) => r.agreement?.status === "signed").length;
  const unclaimed = rows.filter((r) => !r.activated).length;

  return (
    <div data-testid="hub-lenders">
      <PageHead
        eyebrow="Network"
        title="The lending panel"
        lede="Every desk the pingtree can route into — funders, challenger banks, specialist lenders, brokers and angels. Their terms here are what prices the fee book."
        testId="hub-lenders-head"
        actions={<Btn kind="primary" onClick={() => setEditing("new")} testId="hub-lender-new">New lender</Btn>}
      />

      <StatStrip
        testId="hub-lenders-stats"
        items={[
          { k: "Desks on the panel", v: String(rows.length), h: `${active} taking cases`, testId: "hub-lenders-stat-total" },
          { k: "Agreements signed", v: String(signed), h: rows.length ? `${rows.length - signed} outstanding` : "—", testId: "hub-lenders-stat-signed" },
          { k: "Yet to set a password", v: String(unclaimed), h: unclaimed ? "Send them an access link" : "Everyone is in", testId: "hub-lenders-stat-unclaimed" },
        ]}
      />

      <Sec>
        <Panel pad={false} testId="hub-lenders-panel" style={{ overflow: "hidden" }}
               footer={<span>Changing a desk&apos;s fee supersedes any signed agreement and tells them on every channel — history keeps the rate it was written at.</span>}>
          {loading ? (
            <Empty testId="hub-lenders-loading">Reading the panel…</Empty>
          ) : rows.length === 0 ? (
            <Empty testId="hub-lenders-empty">No desks on the panel yet. Add one to start routing.</Empty>
          ) : (
            <Tb testId="hub-lenders-table"
                head={<>
                  <Th style={{ width: "24%" }}>Desk</Th>
                  <Th style={{ width: "19%" }}>Covers</Th>
                  <Th className="r" style={{ width: "13%" }}>Deal band</Th>
                  <Th className="r" style={{ width: "11%" }}>Our terms</Th>
                  <Th style={{ width: "17%" }}>Status</Th>
                  <Th style={{ width: "9%" }}>Access</Th>
                  <Th className="r" style={{ width: "7%" }}>&nbsp;</Th>
                </>}>
              {rows.map((L) => (
                <tr key={L.id} data-testid={`hub-lender-row-${L.id}`}>
                  <Td className="lead" style={{ padding: "20px" }}>
                    <CellMain style={{ fontSize: 14.5 }}>{L.company_name}</CellMain>
                    <CellSub>{L.contact_name || "—"} · {L.email}</CellSub>
                    <CellSub style={{ marginTop: 4 }}>
                      {L.company_type} · {L.delivery_method}
                      {L.turnaround_days != null ? ` · ${L.turnaround_days}d to a decision` : ""}
                    </CellSub>
                    <CellSub style={{ marginTop: 6 }}>
                      <button onClick={() => setAssignFor(L)} data-testid={`hub-lender-manager-${L.id}`}
                              title="The only Connexis contact this desk ever sees"
                              style={{ border: 0, background: "none", padding: 0, font: "inherit", cursor: "pointer",
                                color: managerName(L.account_manager_id) ? "var(--mut)" : "var(--accent-text)",
                                textDecoration: "underline", textDecorationStyle: "dotted" }}>
                        {managerName(L.account_manager_id)
                          ? `Managed by ${managerName(L.account_manager_id)}`
                          : "No manager — assign one"}
                      </button>
                    </CellSub>
                  </Td>
                  <Td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {(L.finance_types || []).slice(0, 3).map((t) => <Lname key={t}>{t}</Lname>)}
                      {(L.finance_types || []).length > 3 && <Lname tone="muted">+{L.finance_types.length - 3}</Lname>}
                      {(L.finance_types || []).length === 0 && <CellSub style={{ margin: 0 }}>Anything</CellSub>}
                    </div>
                    {L.crm?.enabled && (
                      <CellSub style={{ marginTop: 7 }}>
                        <span data-testid={`hub-lender-crm-${L.id}`}>
                          Delivered into {L.crm.provider === "hubspot" ? "HubSpot" : L.crm.provider === "pipedrive" ? "Pipedrive" : "Salesforce"}
                        </span>
                      </CellSub>
                    )}
                  </Td>
                  <Td className="r">
                    <Money raw={`£${shortAmt(L.min_deal_size_gbp)}–£${shortAmt(L.max_deal_size_gbp)}`} />
                  </Td>
                  <Td className="r">
                    <Money raw={["flat", "per_intro"].includes(L.commission_type)
                      ? `£${Math.round(L.commission_flat_gbp || 0).toLocaleString()}`
                      : `${L.commission_pct}%`} />
                    <CellSub>
                      {L.commission_type === "flat" ? "Flat per deal"
                        : L.commission_type === "per_intro" ? "Per intro"
                          : L.commission_type === "income_share" ? "Of their income" : "Of funded"}
                    </CellSub>
                    {L.expected_fee_pct != null && (
                      <CellSub data-testid={`hub-lender-fee-${L.id}`} title={L.expected_fee_source === "desk" ? "Stated by the desk itself" : "Forecast set in the Hub"}>
                        Earns ~{L.expected_fee_pct}%{L.expected_fee_source === "desk" ? " · their figure" : ""}
                      </CellSub>
                    )}
                  </Td>
                  <Td>
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                      <St tone={L.status === "active" ? "mint" : "slate"} testId={`hub-lender-status-${L.id}`}>{L.status}</St>
                      <FlowChip L={L} />
                      <MutedPill summary={L.notify_summary} id={L.id} />
                      {L.agreement?.status === "signed" && <Tag>Signed</Tag>}
                    </div>
                    {L.terms_changed && (
                      <button onClick={() => setAgreementFor(L)} className="btn btn-ghost btn-sm" style={{ marginTop: 9 }}
                              title="Signed agreement is on the old fee — re-issue at the new terms"
                              data-testid={`hub-lender-terms-changed-${L.id}`}>
                        Terms changed — re-issue
                      </button>
                    )}
                  </Td>
                  <Td>
                    <InviteCell invite={L.invite} activated={L.activated} onRegenerate={() => regenerate(L.id)}
                                onSend={() => sendAccessLink(L.id)} testId={`hub-lender-invite-${L.id}`} />
                  </Td>
                  <Td className="r">
                    <div style={{ display: "inline-flex", gap: 2 }}>
                      <IconBtn onClick={() => setAgreementFor(L)} testId={`hub-lender-agreement-${L.id}`}
                               title={L.agreement?.status === "signed" ? `Executed by ${L.agreement.signed_name}` : "Agreement — send, download or countersign"}>
                        <FileSignature className={`size-4 ${L.agreement?.status === "signed" ? "text-teal-600" : ""}`} />
                      </IconBtn>
                      <IconBtn onClick={() => setEditing(L)} testId={`hub-lender-edit-${L.id}`} title="Edit">
                        <Pencil className="size-4" />
                      </IconBtn>
                      <IconBtn onClick={() => setToDelete(L)} testId={`hub-lender-delete-${L.id}`} title="Delete" danger>
                        <Trash2 className="size-4" />
                      </IconBtn>
                    </div>
                  </Td>
                </tr>
              ))}
            </Tb>
          )}
        </Panel>
      </Sec>

      <LenderDialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)} initial={editing === "new" ? null : editing}
                    onSaved={(res) => { setEditing(null); load(); if (res?.change?.changed) setTermsMoved(res); }} />

      <TermsChangePrompt kind="lender" party={termsMoved?.party} change={termsMoved?.change}
                         onOpenChange={(o) => !o && setTermsMoved(null)} onSent={load} />

      <AgreementAdminDialog kind="lender" party={agreementFor} onOpenChange={(o) => !o && setAgreementFor(null)} onChanged={load} />

      <RemoveDialog kind="lender" id={toDelete?.id} name={toDelete?.company_name}
                    open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
                    onDeleted={() => { setRows((rs) => rs.filter((r) => r.id !== toDelete.id)); setToDelete(null); }} />

      <AssignManagerDialog kind="lender" party={assignFor} open={!!assignFor}
                           onOpenChange={(o) => !o && setAssignFor(null)}
                           onDone={() => { loadManagers(); load(); }} />
    </div>
  );
}

function LenderDialog({ open, onOpenChange, initial, onSaved }) {
  const EMPTY = { company_name: "", contact_name: "", email: "", phone: "", company_type: "Lender", finance_types: [], countries_covered: [], min_deal_size_gbp: "", max_deal_size_gbp: "", commission_pct: "", commission_type: "percentage", commission_flat_gbp: "", expected_fee_pct: "", turnaround_days: 3, payment_terms_days: 14, delivery_method: "dashboard", status: "active" };
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!initial) { setForm(EMPTY); return; }
    const clean = Object.fromEntries(Object.entries(initial).map(([k, v]) => [k, v === null || v === undefined ? "" : v]));
    setForm({ ...EMPTY, ...clean,
      min_deal_size_gbp: initial.min_deal_size_gbp || "",
      max_deal_size_gbp: initial.max_deal_size_gbp || "",
      commission_pct: initial.commission_pct || "",
      commission_flat_gbp: initial.commission_flat_gbp || "",
      expected_fee_pct: initial.expected_fee_pct ?? "",
      turnaround_days: initial.turnaround_days ?? "",
      payment_terms_days: Number(initial.payment_terms_days) === 30 ? 30 : 14 });
  }, [initial, open]); // eslint-disable-line
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleFT = (t) => setForm((f) => ({ ...f, finance_types: f.finance_types.includes(t) ? f.finance_types.filter((x) => x !== t) : [...f.finance_types, t] }));
  const toggleCountry = (c) => setForm((f) => ({ ...f, countries_covered: (f.countries_covered || []).includes(c) ? f.countries_covered.filter((x) => x !== c) : [...(f.countries_covered || []), c] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.email.trim()) { toast.error("Company name + email required"); return; }
    const turnaround = parseInt(form.turnaround_days, 10);
    if (!Number.isFinite(turnaround)) {
      toast.error("Add their typical turnaround — every case is fit-scored against it");
      return;
    }
    setBusy(true);
    try {
      // PUT replaces every editable field, so the whole profile goes back — but typed.
      // `initial` nulls were blanked to "" for the text inputs; a blank is not a number or a
      // list, and sending one gets the save rejected (422) for fields the form never shows.
      const payload = { ...form, min_deal_size_gbp: parseFloat(form.min_deal_size_gbp) || 0, max_deal_size_gbp: parseFloat(form.max_deal_size_gbp) || 0, commission_pct: parseFloat(form.commission_pct) || 0, commission_flat_gbp: parseFloat(form.commission_flat_gbp) || 0, commission_type: form.commission_type || "percentage", expected_fee_pct: form.expected_fee_pct === "" ? null : parseFloat(form.expected_fee_pct), payment_terms_days: Number(form.payment_terms_days) === 30 ? 30 : 14,
        turnaround_days: turnaround,
        finance_types: Array.isArray(form.finance_types) ? form.finance_types : [],
        industry_sectors: Array.isArray(form.industry_sectors) ? form.industry_sectors : [],
        countries_covered: Array.isArray(form.countries_covered) ? form.countries_covered : [] };
      const r = isEdit ? await api.put(`/hub/lenders/${initial.id}`, payload)
                       : await api.post("/hub/lenders", payload);
      toast.success(isEdit ? "Lender updated"
        : r.data?.invite_emailed ? "Lender created — set-up link emailed + texted to them"
        : "Lender created — invite link ready to copy (email couldn't be delivered)");
      onSaved({ party: r.data, change: r.data?.terms_change });
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92dvh] w-[calc(100vw-1rem)] sm:w-full overflow-y-auto p-0" data-testid="hub-lender-dialog">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit lender" : "New lender"}</DialogTitle>
            <DialogDescription>Set matching rules, commission and delivery preference.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="mt-4 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Company name *"><Input value={form.company_name} onChange={setField("company_name")} data-testid="lender-company" /></F>
              <F label="Contact name"><Input value={form.contact_name} onChange={setField("contact_name")} /></F>
              <F label="Email *"><Input type="email" value={form.email} onChange={setField("email")} data-testid="lender-email" /></F>
              <F label="Phone"><Input value={form.phone} onChange={setField("phone")} /></F>
              <F label="Type"><Select value={form.company_type} onValueChange={(v) => setForm((f) => ({ ...f, company_type: v }))}><SelectTrigger data-testid="lender-type"><SelectValue /></SelectTrigger><SelectContent>{COMPANY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></F>
              <F label="Delivery"><Select value={form.delivery_method} onValueChange={(v) => setForm((f) => ({ ...f, delivery_method: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{DELIVERY_METHODS.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent></Select></F>
              <F label="Min deal"><MoneyField value={form.min_deal_size_gbp} onChange={(v) => setForm((f) => ({ ...f, min_deal_size_gbp: v }))} compact testId="hub-lender-min-deal" hint="drag or type" /></F>
              <F label="Max deal"><MoneyField value={form.max_deal_size_gbp} onChange={(v) => setForm((f) => ({ ...f, max_deal_size_gbp: v }))} compact testId="hub-lender-max-deal" hint="0 = unlimited" /></F>
              <F label="Connexis fee model" wide>
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                  {[["income_share", "% of their income", "50/50 of the commission they earn"],
                    ["percentage", "% of funded", "share of the facility size"],
                    ["flat", "Flat £ per deal", "same fee whatever the size"],
                    ["per_intro", "Flat £ per intro", "paid when they accept a case"]].map(([k, l, hint]) => (
                    <button key={k} type="button" onClick={() => setForm((f) => ({ ...f, commission_type: k }))}
                            title={hint}
                            className={`rounded-lg border px-2 py-1.5 text-left transition-colors ${(form.commission_type || "percentage") === k ? "bg-[#0055FF]/[0.06] border-[#0055FF]/50 text-[#0055FF]" : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"}`}
                            data-testid={`lender-comm-${k}`}>
                      <span className="block text-[12px] font-semibold leading-tight">{l}</span>
                      <span className="mt-0.5 block text-[10px] leading-tight text-slate-400">{hint}</span>
                    </button>
                  ))}
                </div>
              </F>
              {["income_share", "percentage"].includes(form.commission_type || "percentage") ? (
                <F label={(form.commission_type === "income_share") ? "% of their income" : "% of funded amount"}>
                  <PercentField value={form.commission_pct} onChange={(v) => setForm((f) => ({ ...f, commission_pct: v }))}
                                max={100} presets={form.commission_type === "income_share" ? [25, 40, 50, 60, 75] : [2, 5, 10, 15, 20]}
                                testId="lender-comm-pct"
                                hint={form.commission_type === "income_share" ? "of the commission they earn on the deal" : "of the funded loan value"} />
                </F>
              ) : (
                <F label={form.commission_type === "per_intro" ? "Flat fee (£ / introduction)" : "Flat fee (£ / completed deal)"}>
                  <MoneyField value={form.commission_flat_gbp} onChange={(v) => setForm((f) => ({ ...f, commission_flat_gbp: v }))}
                              ladder="fee" compact testId="lender-comm-flat"
                              hint={form.commission_type === "per_intro" ? "per accepted introduction" : "per completed deal"} />
                </F>
              )}
              <F label="What they usually earn on a deal">
                <PercentField value={form.expected_fee_pct} onChange={(v) => setForm((f) => ({ ...f, expected_fee_pct: v }))}
                              max={20} step={0.25} presets={[1, 1.5, 2.5, 5]}
                              testId="lender-expected-fee-pct"
                              hint={initial?.expected_fee_source === "desk"
                                ? `the desk's own figure${initial?.expected_fee_updated_at ? `, set by them on ${ukDayYear(initial.expected_fee_updated_at)}` : ""} — it syncs here the moment they change it in their portal`
                                : "forecast only — 2.5% if left blank, and the real figure is confirmed per deal"} />
              </F>
              <F label="Payment terms">
                <div className="flex gap-1.5">
                  {[14, 30].map((d) => (
                    <button key={d} type="button" onClick={() => setForm((f) => ({ ...f, payment_terms_days: d }))}
                            className={`flex-1 rounded-lg border px-2 h-9 text-[12px] font-semibold transition-colors ${Number(form.payment_terms_days || 14) === d ? "bg-[#0055FF]/[0.06] border-[#0055FF]/50 text-[#0055FF]" : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"}`}
                            data-testid={`lender-terms-${d}`}>{d} days</button>
                  ))}
                </div>
              </F>
              <F label="Typical turnaround *">
                <CountField value={form.turnaround_days} onChange={(v) => setForm((f) => ({ ...f, turnaround_days: v }))}
                            max={90} suffix="working days to a decision" testId="lender-turnaround" />
              </F>
              <F label="Status"><Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem></SelectContent></Select></F>
            </div>
            <div>
              <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Finance types covered</Label>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="lender-finance-types">
                {FINANCE_TYPES.map((t) => {
                  const active = form.finance_types.includes(t);
                  return (
                    <button type="button" key={t} onClick={() => toggleFT(t)}
                      className={`px-3 py-1.5 rounded-full border text-[12px] font-medium ${active ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                      data-testid={`lender-ft-${t.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Countries funded in</Label>
              <div className="mt-2 flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-1" data-testid="lender-countries">
                {COUNTRIES.map((c) => {
                  const active = (form.countries_covered || []).includes(c);
                  return (
                    <button type="button" key={c} onClick={() => toggleCountry(c)}
                      className={`px-3 py-1.5 rounded-full border text-[12px] font-medium ${active ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                      data-testid={`lender-country-${c.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="hub-lender-submit">{busy ? <Loader2 className="size-4 animate-spin" /> : isEdit ? "Save" : "Create"}</Button>
            </div>
          </form>

          {isEdit && (
            <div className="mt-5 pt-5 border-t border-slate-100 space-y-3">
              <MutedNotice summary={initial.notify_summary} name={initial.company_name} />
              <CapacityCard scope="admin" lenderId={initial.id} />
              <DeclineInsights path={`/hub/lenders/${initial.id}/declines`} days={180} showLenders={false}
                               title={`Why ${initial.company_name} passes`} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InviteCell({ invite, activated, onRegenerate, onSend, testId }) {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const send = async () => { setSending(true); try { await onSend?.(); } finally { setSending(false); } };
  const SendBtn = ({ label }) => (
    <button onClick={send} disabled={sending} className="p-1 rounded hover:bg-slate-100 text-slate-500" title={label} data-testid={`${testId}-send`}>
      {sending ? <Loader2 className="size-3.5 animate-spin" /> : <KeyRound className="size-3.5" />}
    </button>
  );
  if (activated) return (
    <div className="inline-flex items-center gap-1" data-testid={testId}>
      <span className="inline-flex items-center gap-1 text-[11px] text-teal-600 font-semibold"><Check className="size-3" /> Active</span>
      <SendBtn label="Email + text a password reset link" />
    </div>
  );
  if (!invite || !invite.token) return (
    <div className="inline-flex items-center gap-1" data-testid={testId}>
      <button onClick={onRegenerate} className="text-[11.5px] text-[#0055FF] font-semibold" data-testid={`${testId}-regen`}>Generate</button>
      <SendBtn label="Email + text a set-up link now" />
    </div>
  );
  const url = `${(process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "")}/hub-invite/${invite.token}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Invite link copied"); } catch { toast.error("Copy failed"); } };
  return (
    <div className="inline-flex items-center gap-1" data-testid={testId}>
      <button onClick={copy} className="inline-flex items-center gap-1 px-2 h-7 rounded border border-slate-300 hover:border-slate-400 bg-white text-[11px] font-semibold text-slate-700" data-testid={`${testId}-copy`}>
        {copied ? <><Check className="size-3 text-teal-500" /> Copied</> : <><Link2 className="size-3" /> Invite</>}
      </button>
      <SendBtn label="Email + text the set-up link now" />
      <button onClick={onRegenerate} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Regenerate" data-testid={`${testId}-regen`}><RefreshCw className="size-3.5" /></button>
      {invite.expired && <span className="text-[10.5px] text-[#0044CC] ml-1 font-semibold">expired</span>}
    </div>
  );
}

function F({ label, children, wide }) { return <div className={wide ? "sm:col-span-2" : ""}><Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">{label}</Label><div className="mt-1.5">{children}</div></div>; }
function FlowChip({ L }) {
  if (L.leads_paused) return (
    <span data-testid={`hub-lender-paused-${L.id}`} title="This desk has paused new cases from their own portal">
      <St tone="quiet">Paused by them</St>
    </span>
  );
  if (L.cap_count > 0 && L.cap_period) return (
    <span data-testid={`hub-lender-cap-${L.id}`} title={`Capped at ${L.cap_count} cases per ${L.cap_period}`}>
      <Tag>{L.cap_count}/{L.cap_period}</Tag>
    </span>
  );
  return null;
}
function shortAmt(n) { n = Number(n) || 0; if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`; if (n >= 1000) return `${(n / 1000).toFixed(0)}k`; return n.toString(); }
